import { useState, useEffect } from 'react';
import axios from 'axios';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import './Banner.css';
import { Link } from 'react-router-dom';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useLang } from '../../../utils/i18n';

// Maps a 3x3 grid position to the classes needed to place the button
// there, absolutely, independent of the heading/subheading text block.
const buttonPositionClasses = {
  'top-left': 'top-4 left-4',
  'top-center': 'top-4 left-1/2 -translate-x-1/2',
  'top-right': 'top-4 right-4',
  'center-left': 'top-1/2 left-4 -translate-y-1/2',
  'center-center': 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
  'center-right': 'top-1/2 right-4 -translate-y-1/2',
  'bottom-left': 'bottom-4 left-4',
  'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
  'bottom-right': 'bottom-4 right-4',
};

export const PreviousBtn = ({ className, onClick }) => {
  return (
    <div className={className} onClick={onClick}>
      <ArrowBackIosIcon />
    </div>
  )
}

export const NextBtn = ({ className, onClick }) => {
  return (
    <div className={className} onClick={onClick}>
      <ArrowForwardIosIcon />
    </div>
  )
}

// Shown only if the admin hasn't created any banner slides yet, so the
// homepage never looks broken/empty on a fresh install.
const fallbackSlides = [
  {
    heading: "Fresh Groceries, Delivered Fast",
    subheading: "Shop everyday essentials at everyday low prices",
    bg: "bg-gradient-to-r from-primary-green to-emerald-600",
    ctaText: "Shop Now",
    ctaLink: "/products",
  },
];

const Banner = () => {
  const { lang } = useLang();
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const { data } = await axios.get('/api/v1/banner-slides');
        setSlides(data.slides);
      } catch (error) {
        console.error('Failed to load banner slides', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSlides();
  }, []);

  const settings = {
    autoplay: true,
    autoplaySpeed: 3000,
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    prevArrow: <PreviousBtn />,
    nextArrow: <NextBtn />,
  };

  if (loading) {
    return <section className="w-full aspect-[1182/528] rounded-lg shadow-sm bg-gray-100 animate-pulse" />;
  }

  const websiteSlides = slides.filter((s) => s.showOnWebsite !== false);
  const slidesToShow = websiteSlides.length > 0 ? websiteSlides : fallbackSlides;

  return (
    <section className="w-full aspect-[1182/528] rounded-lg shadow-sm relative overflow-hidden">
      <Slider {...settings}>
        {slidesToShow.map((slide, i) => {
          const useZh = lang === 'zh';
          const displayImage = (useZh && slide.imageZh?.url) ? slide.imageZh : slide.image;
          const displayHeading = (useZh && slide.headingZh) ? slide.headingZh : slide.heading;
          const displaySubheading = (useZh && slide.subheadingZh) ? slide.subheadingZh : slide.subheading;
          const displayCtaText = (useZh && slide.ctaTextZh) ? slide.ctaTextZh : slide.ctaText;

          return (
          <div key={slide._id || i}>
            <div className={`w-full aspect-[1182/528] relative overflow-hidden ${slide.bg || ''}`}>
              {displayImage?.url && (
                <img
                  draggable="false"
                  src={displayImage.url}
                  alt={displayHeading}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              )}
              {/* Dark overlay so text stays readable over busy photos —
                  optional per-slide, defaults to on */}
              {displayImage?.url && slide.showOverlay !== false && (
                <div className="absolute inset-0 bg-black/30" />
              )}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <h2 className="text-white text-xl sm:text-3xl font-semibold mb-2">{displayHeading}</h2>
                {displaySubheading && (
                  <div
                    className="text-white text-sm sm:text-base opacity-90 mb-4"
                    dangerouslySetInnerHTML={{ __html: displaySubheading }}
                  />
                )}
              </div>

              {slide.showButton !== false && (
                <Link
                  to={slide.ctaLink || "/products"}
                  className={`absolute ${buttonPositionClasses[slide.buttonPosition] || buttonPositionClasses['center-center']} bg-white text-gray-800 text-sm font-medium px-5 py-2 rounded-sm shadow hover:shadow-lg`}
                >
                  {displayCtaText || "Shop Now"}
                </Link>
              )}
            </div>
          </div>
          );
        })}
      </Slider>
    </section>
  );
};

export default Banner;
